import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from "../services/ApiService";
import AuthService from "../services/AuthService";
import logo from '../assets/images/logo.jpeg';
import Image from '../components/Image';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

const PublicLayout = ({ children }) => {
    const [settings, setSettings] = useState({});
    const [authUser, setAuthUser] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        new_password: ''
    });
    const navigate = useNavigate();

    const getSettings = async () => {
        try {
            const response = await ApiService.request({
                method: "GET",
                url: "/public-settings",
            });
            if (response.data.status) {
                setSettings(response.data.data);
                if (response.data.data.theme_color) {
                    document.documentElement.style.setProperty('--kt-primary', response.data.data.theme_color);
                }
                if (response.data.data.secondary_color) {
                    document.documentElement.style.setProperty('--kt-secondary', response.data.data.secondary_color);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    useEffect(() => {
        getSettings();
        const user = AuthService.getCurrentUser();
        setAuthUser(user);
        if (user) {
            setProfileData(prev => ({ ...prev, name: user.name, email: user.email, phone: user.phone || '' }));
        }
        
        // Listen for storage changes to update user info in real-time
        const handleStorageChange = () => {
            const updatedUser = AuthService.getCurrentUser();
            setAuthUser(updatedUser);
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const handleLogout = () => {
        AuthService.logout();
        navigate('/login?role=customer');
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: '/auth/update-profile', // Assuming this endpoint exists or will be created
                data: profileData
            });
            if (response.data.status) {
                toast.success("Profile updated successfully");
                // Update local storage
                const updatedUser = { ...authUser, ...response.data.data };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setAuthUser(updatedUser);
                setShowProfileModal(false);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update profile");
        }
    };

    const companyLogo = settings?.company_logo || logo;

    const [showDropdown, setShowDropdown] = useState(false);

    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.dropdown')) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    return (
        <div className="d-flex flex-column min-vh-100 bg-light">
            {/* Header */}
            <nav className="navbar navbar-expand-lg navbar-light bg-base-light border-bottom py-0 sticky-top shadow-sm">
                <div className="container-xxl d-flex align-items-center justify-content-between">
                    <Link to="/" className="navbar-brand">
                        <Image alt="Logo" src={companyLogo} style={{ height: '70px', objectFit: 'contain' }} />
                    </Link>
                    
                    <div className="d-flex align-items-center gap-3">
                        {authUser && authUser.role === 'customer' ? (
                            <>
                                <Link to="/appointments" className="btn btn-light btn-sm fw-bold d-flex align-items-center gap-2">
                                    <i className="fas fa-calendar-alt"></i>
                                    Appointments
                                </Link>
                                
                                <div className="dropdown position-relative">
                                    <button 
                                        className={`btn btn-light btn-sm fw-bold dropdown-toggle d-flex align-items-center gap-2 ${showDropdown ? 'show' : ''}`}
                                        type="button" 
                                        onClick={toggleDropdown}
                                        aria-expanded={showDropdown}
                                    >
                                        <i className="fas fa-user-circle fs-4"></i>
                                        {authUser.name}
                                    </button>
                                    <ul className={`dropdown-menu dropdown-menu-end ${showDropdown ? 'show' : ''}`} style={{ right: 0 }}>
                                        <li>
                                            <button className="dropdown-item" onClick={() => { setShowProfileModal(true); setShowDropdown(false); }}>
                                                <i className="fas fa-user-edit me-2"></i> Update Profile
                                            </button>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <button className="dropdown-item text-danger" onClick={handleLogout}>
                                                <i className="fas fa-sign-out-alt me-2"></i> Logout
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </>
                        ) : (
                            <Link to="/login?role=customer" className="btn btn-base btn-sm px-4 fw-bold">Login</Link>
                        )}
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="flex-grow-1 d-flex flex-column py-10">
                <div className="container-xxl flex-grow-1">
                    {children}
                </div>
            </div>

            {/* Footer */}
            <footer className="py-5 bg-white border-top mt-auto">
                <div className="container-xxl d-flex flex-column flex-md-row align-items-center justify-content-between">
                    <div className="text-dark order-2 order-md-1">
                        <span className="text-muted fw-semibold me-1">{new Date().getFullYear()}&copy;</span>
                        <span className="text-gray-800 text-hover-primary fw-bold">{settings?.company_name || 'Saloon System'}</span>
                    </div>
                    <ul className="menu menu-gray-600 menu-hover-primary fw-semibold order-1">
                        <li className="menu-item"><Link to="/" className="menu-link px-2">Home</Link></li>
                        {!authUser && <li className="menu-item"><Link to="/login" className="menu-link px-2">Staff Login</Link></li>}
                    </ul>
                </div>
            </footer>

            {/* Profile Modal */}
            <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Update Profile</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleProfileUpdate}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={profileData.name} 
                                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control 
                                type="email" 
                                value={profileData.email} 
                                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={profileData.phone} 
                                onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                            />
                        </Form.Group>
                        <hr />
                        <Form.Group className="mb-3">
                            <Form.Label>New Password (Optional)</Form.Label>
                            <Form.Control 
                                type="password" 
                                value={profileData.new_password} 
                                onChange={(e) => setProfileData({...profileData, new_password: e.target.value})}
                                placeholder="Leave blank to keep current"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowProfileModal(false)}>
                            Close
                        </Button>
                        <Button variant="primary" type="submit">
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default PublicLayout;
